import React, { useState, useEffect } from 'react';
import { RootStateOrAny, useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { InputLabel, Select, Grid, List, ListItem, ListItemText, Tooltip, FormControl } from '@material-ui/core';
import { setSpecificSpecialEventData } from '../../../redux/actions/newOperationHoursActions'
import { calculateDeliveryTimeBase } from '../../../utils/helpers';
import moment from 'moment';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        formControl: {
            margin: theme.spacing(1),
            minWidth: 120,
        },
        selectEmpty: {
            marginTop: theme.spacing(2),
        },
        root: {
            flexGrow: 1,
            borderBottom: '1px solid',
            borderBottomColor: '#bdbdbd',
            marginTop: 5,
        },
        demo: {
            backgroundColor: '#f5f5f5',
        }
    }),
);

export default function ({ disableAll }) {
    const { t } = useTranslation();
    const classes = useStyles();
    const dispatch = useDispatch();

    const selectedBrand = useSelector((state: RootStateOrAny) => state.UIReducer.selectedBrand);
    const restaurant = useSelector((state: RootStateOrAny) => state.RestaurantsReducer.entity);

    const {
        deliveryHours,
        deliveryTimeOpenMode,
        deliveryTimeCloseMode,
        deliveryTimeOpen,
        deliveryTimeClose,
        isCalculated,
        dtTimeOpen,
        dtTimeClose,
        drTimeOpen,
        drTimeClose,
        drIsOpened24Hours,
        dtIsOpened24Hours,
        drIsClosed,
        dtIsClosed,
        availableTimes,
        specialDrDtTimes,
        isEditing
    } = useSelector((state: RootStateOrAny) => state.NewOperationHoursReducer);

    const [selectOpenSelectedIndex, setSelectOpenSelectedIndex] = useState(0)

    const disabledSelects = disableAll || isCalculated === 'Y';


    // Only used for getValueSelectOpen and getValueSelectClose functions
    const dtTimeOpenIndex = dtIsClosed === 'Y' ? '' : availableTimes.findIndex(elem => elem === dtTimeOpen);
    const drTimeOpenIndex = drIsClosed === 'Y' ? '' : availableTimes.findIndex(elem => elem === drTimeOpen);
    const indexesOpenArray: number[] = [dtTimeOpenIndex, drTimeOpenIndex].filter((elem) => elem > 0);
    const minIndex = Math.min(...indexesOpenArray);

    const dtTimeCloseIndex = dtIsClosed === 'Y' ? '' : specialDrDtTimes.findIndex((elem, index) => index > dtTimeOpenIndex && elem === dtTimeClose);
    const drTimeCloseIndex = drIsClosed === 'Y' ? '' : specialDrDtTimes.findIndex((elem, index) => index > drTimeOpenIndex && elem === drTimeClose);
    const indexesCloseArray: number[] = [dtTimeCloseIndex, drTimeCloseIndex].filter((elem) => elem > 0);
    //

    const getValueSelectOpen = () => {
        if (isCalculated === 'Y') {
            if (indexesOpenArray.length) {
                if (deliveryHours) {
                    if (deliveryHours.brand === selectedBrand && deliveryHours.country == restaurant?.CountryId) {
                        if (dtIsOpened24Hours === 'Y' || drIsOpened24Hours === 'Y') {
                            if (deliveryHours.default24HourTimeOpen !== null) {
                                return deliveryHours.default24HourTimeOpen;
                            } else {
                                return "00:00";
                            }
                        } else {
                            return calculateDeliveryTimeBase(availableTimes[minIndex], deliveryHours.opening, true)
                        }
                    }
                    else {
                        return '';
                    }
                } else {
                    return availableTimes[minIndex];
                }
            }
            else {
                if (dtIsOpened24Hours === 'Y' || drIsOpened24Hours === 'Y') {
                    if (deliveryHours.default24HourTimeOpen !== null) {
                        return deliveryHours.default24HourTimeOpen;
                    } else {
                        return "00:00";
                    }
                }
                return '';
            }
        }
        else {
            return deliveryTimeOpen ? deliveryTimeOpen : '';
        }
    }

    const getValueSelectClose = () => {
        if (isCalculated === 'Y') {
            if (indexesCloseArray.length) {
                const minIndexCloseArrays = Math.min(...indexesCloseArray);
                let maxIndex = Math.max(...indexesCloseArray);

                let maxCloseTime = moment(specialDrDtTimes[maxIndex], 'HH:mm');
                let minCloseTime = moment(specialDrDtTimes[minIndexCloseArrays], 'HH:mm');
                let minOpenTime = moment(availableTimes[minIndex], 'HH:mm');

                if (maxCloseTime.isSameOrBefore(minCloseTime)) {
                    minCloseTime = maxCloseTime;
                }

                if ((minCloseTime.isAfter(minOpenTime) || minCloseTime.isSameOrAfter(moment('06:00', 'HH:mm')))
                    && minIndexCloseArrays > minIndex && (maxIndex >= 51 || !maxCloseTime.isValid())) {
                    maxIndex = minIndexCloseArrays;
                }

                if (deliveryHours) {
                    if (deliveryHours.brand === selectedBrand && deliveryHours.country == restaurant?.CountryId) {
                        if (dtIsOpened24Hours === 'Y' || drIsOpened24Hours === 'Y') {
                            if (deliveryHours.default24HourTimeClose !== null) {
                                return deliveryHours.default24HourTimeClose;
                            } else {
                                return "23:59";
                            }
                        } else {
                            return calculateDeliveryTimeBase(specialDrDtTimes[maxIndex], deliveryHours.closing, false)
                        }
                    }
                    else {
                        return '';
                    }
                } else {
                    return specialDrDtTimes[maxIndex];
                }
            }
            else {
                if (dtIsOpened24Hours === 'Y' || drIsOpened24Hours === 'Y') {
                    if (deliveryHours.default24HourTimeClose !== null) {
                        return deliveryHours.default24HourTimeClose;
                    } else {
                        return "23:59";
                    }
                }
                return '';
            }
        }
        else {
            return deliveryTimeClose ? deliveryTimeClose : '';
        }
    }

    const onChangeSelectOpen = (event) => {
        const select = event.target as HTMLSelectElement;
        if (select.value !== deliveryTimeOpen) {
            dispatch(setSpecificSpecialEventData({
                deliveryTimeOpen: select.value
            }));
        }
    }

    const onChangeSelectClose = (event) => {
        const select = event.target as HTMLSelectElement;
        if (select.value !== deliveryTimeClose) {
            dispatch(setSpecificSpecialEventData({
                deliveryTimeClose: select.value
            }));
        }
    }

    useEffect(() => {
        if (isCalculated === 'Y') {
            dispatch(setSpecificSpecialEventData({
                deliveryTimeOpen: getValueSelectOpen(),
                isEditing: isEditing,
            }));
        }
    }, [isCalculated, dtTimeOpen, drTimeOpen, dtIsOpened24Hours, drIsOpened24Hours, dtIsClosed, drIsClosed])

    useEffect(() => {
        if (isCalculated === 'Y') {
            dispatch(setSpecificSpecialEventData({
                deliveryTimeClose: getValueSelectClose(),
                isEditing: isEditing,
            }));
        }
    }, [isCalculated, dtTimeClose, drTimeClose, dtIsOpened24Hours, drIsOpened24Hours, dtIsClosed, drIsClosed])

    const tooltip = isCalculated === 'Y' ? t('operationHours.isCalculatedMessage') : '';

    return (
        <div className={classes.root}>
            <Grid container spacing={0}>
                <Grid item xs={4} md={4}>
                    <div className={classes.demo}>
                        <List>
                            <ListItem>
                                <ListItemText primary={t('operationHours.deliveryTime')} />
                            </ListItem>
                        </List>
                    </div>
                </Grid>
                <Grid item xs={2} md={2}>
                </Grid>
                <Grid item xs={3} md={3}>
                    {
                        deliveryTimeOpenMode.includes("read") ?
                            <Tooltip title={tooltip}>
                                <FormControl variant="outlined" className={classes.formControl}>
                                    <InputLabel htmlFor="outlined-time-native-simple">{t('operationHours.start')}</InputLabel>
                                    <Select
                                        style={
                                            disabledSelects
                                                ? { pointerEvents: 'none', color: 'grey' }
                                                : undefined
                                        }
                                        native
                                        value={deliveryTimeOpen ? deliveryTimeOpen : ''}
                                        label={t('operationHours.start')}
                                        inputProps={{
                                            name: 'availableTimes',
                                            id: 'outlined-time-native-simple',
                                        }}
                                        onChange={onChangeSelectOpen}
                                        disabled={disabledSelects}
                                    >
                                        {
                                            isCalculated === 'Y' ?
                                                <option value={deliveryTimeOpen ? deliveryTimeOpen : ''}>{deliveryTimeOpen ? deliveryTimeOpen : ''}</option> :
                                                availableTimes?.map((elem, index) => <option key={index} value={elem}>{elem}</option>)
                                        }
                                    </Select>
                                </FormControl>
                            </Tooltip>
                            :
                            null
                    }
                </Grid>
                <Grid item xs={3} md={3}>
                    {
                        deliveryTimeCloseMode.includes("read") ?
                            <Tooltip title={tooltip}>
                                <FormControl variant="outlined" className={classes.formControl}>
                                    <InputLabel htmlFor="outlined-time-native-simple">{t('operationHours.close')}</InputLabel>
                                    <Select
                                        style={
                                            disabledSelects
                                                ? { pointerEvents: 'none', color: 'grey' }
                                                : undefined
                                        }
                                        native
                                        value={deliveryTimeClose ? deliveryTimeClose : ''}
                                        label={t('operationHours.close')}
                                        inputProps={{
                                            name: 'availableTimes',
                                            id: 'outlined-time-native-simple',
                                        }}
                                        onChange={onChangeSelectClose}
                                        disabled={disabledSelects}
                                    >
                                        {
                                            isCalculated === 'Y' ?
                                                <option value={deliveryTimeClose ? deliveryTimeClose : ''}>{deliveryTimeClose ? deliveryTimeClose : ''}</option> :
                                                availableTimes?.filter((elem, index) =>
                                                    index > selectOpenSelectedIndex || index === 0
                                                ).map((elem, index) => (
                                                    <option value={elem}>{elem}</option>
                                                ))
                                        }
                                    </Select>
                                </FormControl>
                            </Tooltip>
                            :
                            null
                    }
                </Grid>
            </Grid>
        </div>
    )
}